import { createContext, useContext, type ReactNode } from "react";

/**
 * The slice of the app currently visible to the user when embedded in the SSO
 * iframe. The iframe is stretched to its full content height and never scrolls
 * itself (the parent page scrolls), so overlays use this to render within the
 * visible viewport instead of centering in the whole document. Null when not
 * embedded (always null in admin).
 */
export type VisibleViewPort = {
  /** Offset, in iframe-document px, where the parent's visible area begins. */
  top: number;
  /** Height, in px, of the portion of the app currently visible to the user. */
  height: number;
};

/**
 * Kept OUT of the session context on purpose: this value updates on every
 * scroll/resize frame while embedded, so folding it into the session context
 * would re-render every `useCustomSession()` consumer (forms, the survey, etc.)
 * hundreds of times. Isolating it here means only overlays that actually need
 * the visible slice subscribe to those updates.
 */
const VisibleViewPortContext = createContext<VisibleViewPort | null>(null);

export const VisibleViewPortProvider = ({
  value,
  children,
}: {
  value: VisibleViewPort | null;
  children: ReactNode;
}) => {
  return (
    <VisibleViewPortContext.Provider value={value}>
      {children}
    </VisibleViewPortContext.Provider>
  );
};

/**
 * Returns the currently visible viewport slice, or null when not embedded in
 * the SSO iframe. Safe to call outside a provider — returns null.
 */
export const useVisibleViewPort = (): VisibleViewPort | null =>
  useContext(VisibleViewPortContext);
